import {
  analyticsApi,
  type ApiKey,
  type Project,
  projectApi,
} from '@shared/api-client';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, UserPlus } from 'lucide-react';
import * as React from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatRelative } from '@/lib/utils';

const endpointFor = (publicKey: string) => {
  const stage = publicKey.match(/^gs_pk_([a-z0-9-]+)_/)?.[1] ?? 'prod';
  const domain = stage === 'prod' ? 'gainsignal.ai' : `${stage}.gainsignal.ai`;
  return `https://ingest.${domain}/v1/events`;
};

const registrationBody = (publicKey: string) => `{
  "project_key": ${JSON.stringify(publicKey)},
  "event": "signup_completed",
  "user_id": "YOUR_INTERNAL_USER_ID",
  "idempotency_key": "signup:YOUR_INTERNAL_USER_ID",
  "occurred_at": "ACCOUNT_CREATED_AT_ISO_8601",
  "properties": { "provider": "email" },
  "test": false
}`;

const aiPromptFor = (publicKey: string) => {
  const endpoint = endpointFor(publicKey);
  return `Add GainSignal registration tracking to this application.

First inspect the codebase and find the authoritative server-side path where a new account is successfully and durably created. Do not track sign-ins, session creation, page views, failed registrations, or client button clicks.

After a new account has been committed, send a best-effort POST request to:
${endpoint}

Use this JSON contract:
${registrationBody(publicKey)}

Implementation requirements:
- Replace placeholders with the stable internal user ID and the account's real creation timestamp.
- Keep idempotency_key deterministic as signup:<internal-user-id>. Retries are safe.
- Set properties.provider only when the registration provider is already known.
- Do not send email, name, profile data, access tokens, or other personal data.
- Run this server-side with a short timeout. A GainSignal timeout or failure must never fail or delay registration.
- Reuse the project's existing background-job or after-commit mechanism when one exists; do not introduce a recursive queue flow.
- Keep the project key exactly as shown. It is an append-only project identifier, not an administrative credential.
- Add focused tests for one successful signup, duplicate delivery, sign-in exclusion, and analytics failure isolation.

After implementation, send one synthetic request with test set to true and a unique test user ID. Do not enable test on live registration events. Report the files changed and the test result.`;
};

const curlFor = (publicKey: string) => `curl --request POST \\
  --url ${endpointFor(publicKey)} \\
  --header 'content-type: application/json' \\
  --data '${registrationBody(publicKey).replaceAll("'", "'\\''")}'`;

const CopyBlock = ({ value, label }: { value: string; label: string }) => {
  const [copied, setCopied] = React.useState(false);
  const preRef = React.useRef<HTMLPreElement>(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      const node = preRef.current;
      if (!node) {
        return;
      }
      const range = document.createRange();
      range.selectNodeContents(node);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  return (
    <div className="relative">
      <pre
        ref={preRef}
        className="max-h-80 overflow-auto rounded-lg border border-border bg-surface-muted p-4 pr-12 font-mono text-xs leading-relaxed text-ink"
      >
        <code>{value}</code>
      </pre>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        className="absolute right-2 top-2 size-8"
        onClick={copy}
        aria-label={`Copy ${label}`}
      >
        {copied ? <Check className="text-positive" /> : <Copy />}
      </Button>
    </div>
  );
};

const currentKey = (apiKeys: ApiKey[] | undefined) =>
  apiKeys?.find((key) => !key.revokedAt);

export const RegistrationTrackingSection = ({
  project,
}: {
  project: Project;
}) => {
  const keysQuery = useQuery({
    queryKey: ['api-keys', project.id],
    queryFn: () => projectApi.listApiKeys(project.id),
  });
  const statusQuery = useQuery({
    queryKey: ['registration-tracking-status', project.id],
    queryFn: () => analyticsApi.getRegistrationTrackingStatus(project.id),
  });
  const apiKey = currentKey(keysQuery.data?.apiKeys);
  const status = statusQuery.data;
  const live = status?.status === 'live';
  const testDetected = status?.status === 'test_detected';
  const detail = live
    ? `${status.signups30d.toLocaleString()} in 30 days${status.lastSignupAt ? ` · last ${formatRelative(status.lastSignupAt)}` : ''}`
    : testDetected && status?.lastTestAt
      ? `Test received ${formatRelative(status.lastTestAt)}`
      : 'No registration events yet';

  return (
    <section>
      <div className="flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 rounded-lg bg-surface-muted p-2 text-ink-muted">
            <UserPlus className="size-4" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold text-ink">
                Track registrations
              </h2>
              <Badge
                tone={live ? 'success' : testDetected ? 'accent' : 'neutral'}
              >
                {live
                  ? 'Live'
                  : testDetected
                    ? 'Test received'
                    : 'Not connected'}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-ink-muted">{detail}</p>
          </div>
        </div>

        <Dialog>
          <DialogTrigger asChild>
            <Button variant="secondary" size="sm">
              {live || testDetected ? 'View setup' : 'Set up'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Track new registrations</DialogTitle>
              <DialogDescription>
                Add one server event after an account is created.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              {!apiKey ? (
                <Alert tone="warning" title="Create an API key first">
                  Use the API keys section above, then reopen this setup.
                </Alert>
              ) : (
                <Tabs defaultValue="ai">
                  <TabsList>
                    <TabsTrigger value="ai">Install with AI</TabsTrigger>
                    <TabsTrigger value="api">API</TabsTrigger>
                  </TabsList>
                  <TabsContent value="ai" className="space-y-3">
                    <p className="text-sm text-ink-muted">
                      Paste this into your coding agent from the project root.
                    </p>
                    <CopyBlock
                      value={aiPromptFor(apiKey.publicKey)}
                      label="AI prompt"
                    />
                  </TabsContent>
                  <TabsContent value="api" className="space-y-3">
                    <p className="text-sm text-ink-muted">
                      Send this only after account creation succeeds. Reuse the
                      same idempotency key when retrying.
                    </p>
                    <CopyBlock
                      value={curlFor(apiKey.publicKey)}
                      label="API example"
                    />
                  </TabsContent>
                </Tabs>
              )}

              {statusQuery.isError ? (
                <Alert
                  className="mt-4"
                  tone="error"
                  title="Could not verify setup"
                >
                  Try again in a moment.
                </Alert>
              ) : live ? (
                <Alert
                  className="mt-4"
                  tone="success"
                  title="Registration tracking is live"
                >
                  {detail}
                </Alert>
              ) : testDetected ? (
                <Alert
                  className="mt-4"
                  tone="success"
                  title="Test event received"
                >
                  Enable the live call in your registration path when ready.
                </Alert>
              ) : null}
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                loading={statusQuery.isFetching}
                onClick={() => void statusQuery.refetch()}
              >
                Verify setup
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};
